/**
 * Light and dark theme selection.
 *
 * Three settings are offered: 'light', 'dark', and 'system' - which is the
 * default, and follows the operating system.
 *
 * The stylesheet, not this module, decides what 'system' looks like: it defines
 * the light palette on `:root` and the dark one inside a prefers-color-scheme
 * query. So 'system' is expressed by removing the `data-theme` attribute
 * altogether rather than by writing a resolved value into it. That is what keeps
 * the first paint correct - the operating system's choice applies before any of
 * this has run - and it means the theme still follows the system while the page
 * is open, with no listener involved.
 *
 * An explicit choice is written to `data-theme`, which both palettes are written
 * to beat.
 */
const STORAGE_KEY = 'webchat.theme';

/** The settings a user can choose between, in the order they are offered. */
export const THEMES = ['system', 'light', 'dark'];

function read() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

let preference = read();

/** The chosen setting: 'system', 'light' or 'dark'. */
export function getTheme() {
  return preference;
}

/** What the operating system currently asks for. */
export function systemTheme() {
  if (typeof matchMedia !== 'function') return 'light';
  return matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** The theme actually on screen, with 'system' resolved. */
export function effectiveTheme() {
  return preference === 'system' ? systemTheme() : preference;
}

/**
 * Writes the current setting to the document. Removing the attribute for
 * 'system' hands the decision back to the stylesheet's media query.
 */
export function applyTheme() {
  const root = globalThis.document?.documentElement;
  if (!root) return preference;
  if (preference === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', preference);
  paintBrowserChrome();
  return preference;
}

/**
 * Keeps the browser's own furniture - notably the address bar on a phone - the
 * same colour as the page. The value is read back out of the stylesheet rather
 * than written here, so the palette has exactly one home.
 */
function paintBrowserChrome() {
  const doc = globalThis.document;
  const meta = doc?.querySelector('meta[name="theme-color"]');
  if (!meta || typeof getComputedStyle !== 'function') return;
  const background = getComputedStyle(doc.documentElement).getPropertyValue('--bg').trim();
  if (background) meta.setAttribute('content', background);
}

/** Chooses a theme, remembers it, and applies it. Unknown values are ignored. */
export function setTheme(value) {
  if (!THEMES.includes(value)) return preference;
  preference = value;
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* storage may be unavailable; the choice still applies to this page */
  }
  return applyTheme();
}

/**
 * Calls `listener` when the operating system's preference changes, but only
 * while the user is following it - a page showing an explicit choice has nothing
 * to update. Nothing needs repainting either way, since the stylesheet handles
 * the colours; this exists so the interface can keep its own labels honest.
 */
export function onSystemThemeChange(listener) {
  if (typeof matchMedia !== 'function') return () => {};
  const query = matchMedia('(prefers-color-scheme: dark)');
  const handler = () => {
    if (preference === 'system') listener(systemTheme());
  };
  query.addEventListener('change', handler);
  return () => query.removeEventListener('change', handler);
}

export default { THEMES, getTheme, setTheme, applyTheme, effectiveTheme, systemTheme };
